import { configureStore, ThunkAction } from '@reduxjs/toolkit';
import { CurriedGetDefaultMiddleware } from '@reduxjs/toolkit/dist/getDefaultMiddleware';
import {
  Action,
  combineReducers,
  Reducer,
  ReducersMapObject,
  Store,
} from 'redux';

import appSlice from './slices/appSlice';
import userSlice from './slices/users';
import campaignDataSlice from './slices/campaignDataSlice';
import marketDataSlice from './slices/marketDataSlice';
import aggregatedMarketDataSlice from './slices/aggregatedMarketDataSlice';
import campaignListSlice from './slices/campaignListSlice';
import marketsListSlice from './slices/marketsListSlice';
import marketsByIDSlice from './slices/marketsByIDSlice';
import campaignsByIDSlice from './slices/campaignsByIDSlice';
import campaignDataForBaseKPISlice from './slices/campaignDataForBaseKPISlice';
import trackingDataSlice from './slices/trackingDataSlice';
import advertisersListSlice from './slices/admin/advertisersListSlice';
import trucksListSlice from './slices/admin/trucksListSlice';
import zonesListSlice from './slices/admin/zonesListSlice';
import roadSegmentsSlice from './slices/roadSegmentsSlice';
import usersListSlice from './slices/admin/usersListSlice';
import zipCodesSlice from './slices/zipCodesSlice';
import gpsDataSlice from './slices/gpsDataSlice';
import selectedButtonSlice from './slices/selectedButtonSlice';
import { baseApi } from './services/baseApi';

interface AppStore extends Store {
  asyncReducers: ReducersMapObject;
  injectReducer: (key: string, reducer: Reducer) => void;
}

// Define the Reducers that will always be present in the application
const staticReducers = {
  app: appSlice,
  user: userSlice,
  campaign: campaignDataSlice,
  market: marketDataSlice,
  aggregatedMarket: aggregatedMarketDataSlice,
  campaignList: campaignListSlice,
  campaignsByID: campaignsByIDSlice,
  campaignDataForBaseKPI: campaignDataForBaseKPISlice,
  marketsList: marketsListSlice,
  marketsByID: marketsByIDSlice,
  trackingData: trackingDataSlice,
  advertisersList: advertisersListSlice,
  trucksList: trucksListSlice,
  zonesList: zonesListSlice,
  roadSegments: roadSegmentsSlice,
  usersListSlice: usersListSlice,
  zipCodes: zipCodesSlice,
  gpsDataSlice: gpsDataSlice,
  selectedButtonSlice: selectedButtonSlice,
  [baseApi.reducerPath]: baseApi.reducer,
};

let store: AppStore = {
  ...configureStore({
    reducer: staticReducers,
    middleware: (getDefaultMiddleware: CurriedGetDefaultMiddleware) =>
      getCustomMiddleware(getDefaultMiddleware).concat([baseApi.middleware]),
  }),
  asyncReducers: {},
  injectReducer: (key: string, asyncReducer: Reducer) => {
    store.asyncReducers[key] = asyncReducer;
    store.replaceReducer(createReducer(store.asyncReducers));
  },
};

function createReducer(asyncReducers = {}) {
  return combineReducers({
    ...staticReducers,
    ...asyncReducers,
  });
}

function getCustomMiddleware(
  getDefaultMiddleware: CurriedGetDefaultMiddleware,
) {
  const devConfig = {
    immutableCheck: {
      ignoredPaths: ['carto.viewportFeatures'],
    },
    serializableCheck: {
      ignoredPaths: ['carto.viewportFeatures'],
      ignoredActions: ['carto/setViewportFeatures'],
    },
  };

  const prodConfig = {
    immutableCheck: false,
    serializableCheck: false,
  };

  const isProductionEnv = process.env.NODE_ENV === 'production';

  return getDefaultMiddleware(isProductionEnv ? prodConfig : devConfig);
}

export default store;

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
